import { AgGridColumnProps } from "ag-grid-react";
import { asCurrency, asDate } from "lqx-react/src";
import { getAssetErrors, getAssetStatus, getCellColor } from "../components/assets-table";
import { asTimelessDateUTC } from "lqx-react/lib";

export enum FilterName {
    PayorCompany = 'payorCompanies',
    PayorBankName = 'payorBankNames',
    PayorBankAlias = 'payorBankAliases',
    PayorBankAccountNumber = 'payorBankAccountNumbers',
    PayeeBankBranchCode = 'PayorBankBranchCodes',
    PayeeCompany = 'payeeCompanies',
    PayeeBankName = 'payeeBankNames',
    PayeeBankAlias = 'payeeBankAliases',
    PayeeBankAccountNumber = 'payeeBankAccountNumbers',
    PayorBankBranchCode = 'payeeBankBranchCodes',
    PaymentDate = 'paymentDates',
    PaymentCurrency = 'paymentCurrencies',
    PaymentAmount = 'paymentAmounts',
    SupplierName = 'suppliers',
    BuyerName = 'buyers',
    InvoiceNumber = 'invoiceNumbers',
    InvoiceDate = 'invoiceDates',
    InvoiceDueDate = 'invoiceDueDates',
    FaceValue = 'faceValues',
    Currency = 'currencies',
    AppliedAmount = 'appliedAmounts',
    PaymentReferenceNumber = 'paymentReferenceNumbers',
}

export enum FilterTypeRemittanceAdvices {
    All = 0,
    ValidationCategory,
    AssetState,
    PayorCompany,
    PayorBankName,
    PayorBankAlias,
    PayorBankAccountNumber,
    PayorBankBranchCode,
    PayeeCompany,
    PayeeBankName,
    PayeeBankAlias,
    PayeeBankAccountNumber,
    PayeeBankBranchCode,
    PaymentDate,
    PaymentCurrency,
    PaymentAmount,
    PaymentReferenceNumber,
    SupplierName,
    BuyerName,
    InvoiceNumber,
    InvoiceDate,
    InvoiceDueDate,
    FaceValue,
    Currency,
    AppliedAmount
}

export enum RemittanceAdvicesSortingBase {
    Id = 0,
    CreatedOn,
    UpdatedOn,
    ValidationCategory,
    AssetState,
    PayorCompany,
    PayorBankName,
    PayorBankAlias,
    PayorBankAccountNumber,
    PayorBankBranchCode,
    PayeeCompany,
    PayeeBankName,
    PayeeBankAlias,
    PayeeBankAccountNumber,
    PayeeBankBranchCode,
    PaymentDate,
    PaymentCurrency,
    PaymentAmount,
    PaymentReferenceNumber,
    SupplierName,
    BuyerName,
    InvoiceNumber,
    InvoiceDate,
    InvoiceDueDate,
    FaceValue,
    Currency,
    AppliedAmount,
    WriteOff,
    Details
}

export enum RemittanceAdvicesTableColumns {
    Status = 0,
    PayorCompany,
    PayorBankName,
    PayorBankAlias,
    PayorBankAccountNumber,
    PayorBankBranchCode,
    PayeeCompany,
    PayeeBankName,
    PayeeBankAlias,
    PayeeBankAccountNumber,
    PayeeBankBranchCode,
    PaymentDate,
    PaymentCurrency,
    PaymentAmount,
    SupplierName,
    BuyerName,
    InvoiceNumber,
    InvoiceDate,
    InvoiceDueDate,
    FaceValue,
    Currency,
    AppliedAmount,
    PaymentReferenceNumber,
    WriteOff,
    Details,
    Errors
}

export const RemittanceAdviceTableColumns: AgGridColumnProps[] = [
    {
        colId: 'CheckBox',
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: true
    },
    {
        colId: RemittanceAdvicesTableColumns.Status.toString(),
        headerName: "Status",
        filter: 'agSetColumnFilter',
        filterParams: {
            values: ['Pass Validation - New', 'Pass Validation - Modified', 'Require Review', 'Duplicated', 'Conflict', 'Saved for Later', 'Digitized', 'Modifies Existing']
        },
        valueGetter: params => getAssetStatus(params.data),
        cellStyle: params => { return { color: getCellColor(params.data) } },
        sortable: true
    },
    {
        colId: RemittanceAdvicesTableColumns.Errors.toString(),
        headerName: "Errors",
        valueGetter: params => getAssetErrors(params.data)
    },
    {
        colId: RemittanceAdvicesTableColumns.PayorCompany.toString(),
        headerName: "Payor Company",
        field: "changes.payor.company.name",
        filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true

    },
    {
        colId: RemittanceAdvicesTableColumns.PayeeCompany.toString(),
        headerName: "Payee Company",
        field: "changes.payee.company.name",
        filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: RemittanceAdvicesTableColumns.PaymentDate.toString(),
        headerName: "Payment Date",
        field: "changes.paymentDate",
        filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        valueFormatter: asTimelessDateUTC,
        sortable: true
    },
    {
        colId: RemittanceAdvicesTableColumns.PaymentCurrency.toString(),
        headerName: "Payment Currency",
        field: "changes.paymentCurrency",
        filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: RemittanceAdvicesTableColumns.PaymentAmount.toString(),
        headerName: "Payment Amount",
        field: "changes.paymentAmount",
        cellStyle: { textAlign: "right" },
        valueFormatter: asCurrency,
        filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: RemittanceAdvicesTableColumns.SupplierName.toString(),
        headerName: "Supplier Name",
        field: "changes.invoice.supplier.name",
        filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: RemittanceAdvicesTableColumns.BuyerName.toString(),
        headerName: "Buyer Name",
        field: "changes.invoice.buyer.name",
        filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: RemittanceAdvicesTableColumns.InvoiceNumber.toString(),
        headerName: "Invoice Number",
        field: "changes.invoice.number",
        filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: RemittanceAdvicesTableColumns.InvoiceDate.toString(),
        headerName: "Invoice Date",
        field: "changes.invoice.invoiceDate",
        filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        valueFormatter: asTimelessDateUTC,
        sortable: true
    },
    {
        colId: RemittanceAdvicesTableColumns.InvoiceDueDate.toString(),
        headerName: "Invoice Due Date",
        field: "changes.invoice.invoiceDueDate",
        filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        valueFormatter: asTimelessDateUTC,
        sortable: true
    },
    {
        colId: RemittanceAdvicesTableColumns.FaceValue.toString(),
        headerName: "Face Value",
        field: "changes.invoice.faceValue",
        cellStyle: { textAlign: "right" },
        valueFormatter: asCurrency,
        filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: RemittanceAdvicesTableColumns.Currency.toString(),
        headerName: "Currency",
        field: "changes.invoice.currency",
        filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: RemittanceAdvicesTableColumns.AppliedAmount.toString(),
        headerName: "Applied Amount",
        field: "changes.invoice.appliedAmount",
        cellStyle: { textAlign: "right" },
        valueFormatter: asCurrency,
        filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: RemittanceAdvicesTableColumns.PaymentReferenceNumber.toString(),
        headerName: "Payment Reference Number",
        field: "changes.paymentReferenceNumber",
        filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: RemittanceAdvicesTableColumns.PayorBankName.toString(),
        headerName: "Payor Bank Name",
        field: "changes.payor.bankName",
        filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: RemittanceAdvicesTableColumns.PayorBankAlias.toString(),
        headerName: "Payor Bank Alias",
        field: "changes.payor.bankAlias",
        filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: RemittanceAdvicesTableColumns.PayorBankAccountNumber.toString(),
        headerName: "Payor Bank Account Number",
        field: "changes.payor.bankAccountNumber",
        filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: RemittanceAdvicesTableColumns.PayorBankBranchCode.toString(),
        headerName: "Payor Bank Branch Code",
        field: "changes.payor.bankBranchCode",
        filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: RemittanceAdvicesTableColumns.PayeeBankName.toString(),
        headerName: "Payee Bank Name",
        field: "changes.payee.bankName",
        filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: RemittanceAdvicesTableColumns.PayeeBankAlias.toString(),
        headerName: "Payee Bank Alias",
        field: "changes.payee.bankAlias",
        filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: RemittanceAdvicesTableColumns.PayeeBankAccountNumber.toString(),
        headerName: "Payee Bank Account Number",
        field: "changes.payee.bankAccountNumber",
        filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: RemittanceAdvicesTableColumns.PayeeBankBranchCode.toString(),
        headerName: "Payee Bank Branch Code",
        field: "changes.payee.bankBranchCode",
        filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: RemittanceAdvicesTableColumns.WriteOff.toString(),
        headerName: "Write Off",
        field: "changes.invoice.writeOff",
        filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    },
    {
        colId: RemittanceAdvicesTableColumns.Details.toString(),
        headerName: "Details",
        field: "changes.invoice.details",
        filter: 'agTextColumnFilter',
        filterParams: {
            filterOptions: ['contains'],
            suppressAndOrCondition: true
        },
        sortable: true
    }
]
